import React from 'react'
import { GoogleLogin, GoogleLoginResponse, GoogleLoginProps } from 'react-google-login'
import {
  useRegisterCustomerMutation,
  OAuthType,
  AuthenticatedUserFragment
} from '#src/generated/graphql'
import GoogleIconMark from './google-iconmark.svg'
import config from '#src/config'
import classnames from 'classnames'
import styles from './styles.css'

interface GoogleOAuthButtonProps
  extends Omit<GoogleLoginProps, 'clientId' | 'responseType' | 'onSuccess' | 'onFailure'> {
  wrapperClassName?: string
  referralCodeId?: string | null
  onSuccess: (user: AuthenticatedUserFragment) => void
  onFailure: (error: string) => void
}

const GoogleOAuthButton: React.SFC<GoogleOAuthButtonProps> = ({
  className,
  wrapperClassName,
  referralCodeId,
  onSuccess,
  onFailure,
  ...rest
}) => {
  const [registerCustomer] = useRegisterCustomerMutation()

  /**
   * After successfully completing the Google OAuth flow, submit the ID token to our API
   *   for authentication.
   */
  const handleSuccess = (response: GoogleLoginResponse) => {
    registerCustomer({
      variables: {
        input: {
          firstName: response.getBasicProfile().getGivenName(),
          lastName: response.getBasicProfile().getFamilyName(),
          email: response.getBasicProfile().getEmail(),
          externalOAuthType: OAuthType.GOOGLE,
          externalOAuthToken: response.getAuthResponse().id_token,
          referralCodeId
        }
      }
    }).then(response => {
      const errors = response.errors

      // Handle failures at the GraphQL level
      if (errors) {
        onFailure('Google authentication failed!')
        return
      }

      const payload = response.data!.registerCustomer

      if (payload.user) {
        onSuccess(payload.user)
      } else {
        onFailure(
          (payload.errors && payload.errors.join('\n')) || 'Facebook authentication failed!'
        )
      }
    })
  }

  /**
   * Handle failures from the Google OAuth flow itself.
   */
  const handleFailure = (failure: { error: string }) => {
    switch (failure.error) {
      // TODO: Should we surface different messages for these error codes?
      case 'idpiframe_initialization_failed':
      case 'popup_closed_by_user':
      case 'access_denied':
      case 'immediate_failed':
      default:
        onFailure('Google authentication failed!')
    }
  }

  return (
    <div className={classnames(styles.wrapper, wrapperClassName)}>
      <GoogleLogin
        className={classnames(styles.button, className)}
        clientId={config.get('google.client_id')}
        responseType="id_token"
        onSuccess={response => handleSuccess(response as GoogleLoginResponse)}
        onFailure={handleFailure}
        {...rest}
      >
        <GoogleIconMark className={styles.logo} /> Continue with Google
      </GoogleLogin>
    </div>
  )
}

export default GoogleOAuthButton
